<template>
	<div data-component="link-data-form">
		<validation-observer
			data-element="form"
			tag="form"
			ref="form"
		>
			<input-field
				name="Name"
				type="text"
				rules="required"
				:value="getName"
				@input="changeName"
			/>
			<checkbox-field
				name="Enabled"
				:checked="getEnabled"
				@change="changeEnabled"
			/>
			<div
				@click="addPicker"
				v-click-outside="removePicker"
			>
				<input-field
					@focus="addPicker"
					name="Expiration date"
					type="text"
					readonly
					:value="getShadowDate"
				/>
				<date-picker
					sync
					:value="getExpirationDate"
					:show="showPicker"
					@input="changeExpirationDate"
				/>
			</div>
			<div data-element="time-periods-list">
				<v2-Button
					v-for="(period, index) in getTimePeriods"
					:key="index"
					fit
					size="xs"
					class="time-period-button"
					@click="setTimePeriod(period)"
				>
					{{ period.label }}
				</v2-Button>
			</div>
			<checkbox-field
				name="No expiration date"
				:checked="getNoExpiration"
				@change="changeNoExpiration"
			/>
			<select-field
				name="Accessibility"
				:options="getAccessibilityOptions"
				:disabled="getAccessibilityChangeDisabled"
				:value="getAccessibleBy"
				@input="changeAccessibleBy"
			/>
		</validation-observer>
	</div>
</template>

<script>

	import InputField from '@/components/forms/InputField.vue';
	import CheckboxField from '@/components/forms/CheckboxField.vue';
	import DatePicker from '@/components/forms/DatePicker.vue';
	import V2Button from '@/components/v2/ui/V2Button';
	import SelectField from '@/components/forms/SelectField';
	import { DEAL_LINK_ACCESSIBILITY_OPTIONS } from '@/consts';
	import { capitalCase } from 'change-case';

	export default {
		components: {
			DatePicker,
			InputField,
			CheckboxField,
			V2Button,
			SelectField
		},
		mixins: [],
		props: {
			value: {
				type: Object,
				default: () => ({
					name: '',
					enabled: true,
					expirationDate: null,
					accessibleBy: DEAL_LINK_ACCESSIBILITY_OPTIONS.PAYING_SUBSCRIBERS,
					token: null,
					deal_ids: []
				})
			},
			isWaiting: {
				type: String,
				default: undefined
			}
		},
		data: () => ({
			showPicker: false
		}),
		computed: {
			getName () {
				return this.value?.name || '';
			},
			getEnabled () {
				return this.value?.enabled || false;
			},
			getNoExpiration () {
				return this.value?.expirationDate === null;
			},
			getExpirationDate () {
				const expirationDate = this.value.expirationDate;
				if (expirationDate === null) {
					return '';
				}
				return expirationDate ? this.$store.getters['auth/getTimeInFormat']({
					time: expirationDate,
					format: 'YYYY-MM-DD HH:mm'
				}) : '';
			},
			getShadowDate () {
				const expirationDate = this.value.expirationDate;
				return expirationDate ? this.getDateTime(expirationDate) : '';
			},
			getTimePeriods () {
				return [
					{
						label: '1 Day',
						value: 1,
						unit: 'day'
					},
					{
						label: '1 Week',
						value: 1,
						unit: 'week'
					},
					{
						label: '1 Month',
						value: 1,
						unit: 'month'
					}
				];
			},
			getAccessibleBy () {
				return this.value.accessibleBy;
			},
			getAccessibilityChangeDisabled () {
				return !this.$store.getters['auth/getIsAdmin'];
			},
			getAccessibilityOptions () {
				return Object.values(DEAL_LINK_ACCESSIBILITY_OPTIONS).map(option => {
					return {
						text: capitalCase(option),
						value: option
					};
				});
			}
		},
		created () {},
		methods: {
			changeName (name) {
				this.$emit('input', {
					...this.value,
					name
				});
			},
			changeEnabled (enabled) {
				this.$emit('input', {
					...this.value,
					enabled
				});
			},
			changeExpirationDate (value) {
				this.$emit('input', {
					...this.value,
					expirationDate: value ? this.getISO(value) : null
				});
			},
			setTimePeriod (period) {
				const now = new Date();
				now.setSeconds(0);
				const value = this.$store.getters['auth/getISOTimeStringAdd']({
					time: now,
					value: period.value,
					unit: period.unit,
					format: 'YYYY-MM-DD HH:mm'
				});
				this.changeExpirationDate(value);
			},
			changeNoExpiration (value) {
				if (value) {
					this.$emit('input', {
						...this.value,
						expirationDate: null
					});
				}
			},
			changeAccessibleBy (accessibleBy) {
				this.$emit('input', {
					...this.value,
					accessibleBy
				});
			},
			getISO (value) {
				return this.$store.getters['auth/getISOTimeString']({
					time: value
				});
			},
			getDateTime (value) {
				return this.$store.getters['auth/getTimeInLocalTimezone']({
					time: value
				});
			},
			addPicker () {
				this.showPicker = true;
			},
			removePicker () {
				this.showPicker = false;
			},
			validate () {
				return this.$refs.form.validate();
			},
			resetForm () {
				this.$emit('input', {
					...this.value,
					name: '',
					enabled: true,
					expirationDate: null,
					accessibleBy: DEAL_LINK_ACCESSIBILITY_OPTIONS.PAYING_SUBSCRIBERS
				});
			}
		}
	};

</script>

<style lang="scss" scoped>
	[data-component='link-data-form'] {
		[data-element='time-periods-list'] {
			display: flex;

			.time-period-button:not(:last-child) {
				margin-right: 10px;
			}
		}

		@media (min-width:1080px) {
			[data-component='select-field'] {
				max-width: 320px;
			}
		}
	}
</style>
